import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { Check, ChevronLeft, PencilLine, ScanLine, Smartphone } from 'lucide-react-native';
import { useState } from 'react';
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import {
  ContactForm,
  emptyContactForm,
  type ContactFormState,
} from '@/components/system/ContactForm';
import { StickyBottomBar } from '@/components/StickyBottomBar';
import { Fonts, Palette, Spacing } from '@/constants/theme';
import { addContact } from '@/lib/contacts';
import { addDocument } from '@/lib/documents';
import { persistPickedFile } from '@/lib/files';
import { goBackOrTo } from '@/lib/nav';
import { scanBusinessCardBase64 } from '@/lib/ocr';
import { getActiveProperty } from '@/lib/tasks';

export default function NewContact() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const [form, setForm] = useState<ContactFormState>(emptyContactForm());
  const [saving, setSaving] = useState(false);
  // Wave 62 (user ask): before dropping people into the form, offer the
  // faster path — importing from the phone's contacts. Native only; the
  // browser can't read device contacts, so web goes straight to typing.
  const [sourceChooserOpen, setSourceChooserOpen] = useState(Platform.OS !== 'web');
  const [scanning, setScanning] = useState(false);
  const [scanHint, setScanHint] = useState<string | null>(null);
  // The scanned card photo — persisted as a Document on save and linked
  // via contact.card_doc_id so the contact page can show the card.
  const [cardUri, setCardUri] = useState<string | null>(null);

  const runScan = async (source: 'camera' | 'library') => {
    setScanHint(null);
    let asset: ImagePicker.ImagePickerAsset | undefined;
    try {
      if (source === 'camera') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchCameraAsync({ quality: 0.7, base64: true });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.7,
          base64: true,
        });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      }
    } catch (err) {
      void notice({ title: 'Picker failed', message: String(err), tone: 'error' });
      return;
    }

    const base64 = asset?.base64 ?? null;
    if (!base64 || !asset) {
      void notice({
        title: "Couldn't read photo",
        message: 'Your photo library returned no image data. Try a different file.',
        tone: 'error',
      });
      return;
    }

    // Keep the photo even if OCR whiffs — the card image itself is the
    // thing worth saving.
    setCardUri(asset.uri);
    setScanning(true);
    try {
      const result = await scanBusinessCardBase64(base64);
      if (!result.ok) {
        if (result.error.kind === 'no-key') {
          setScanHint('Card photo attached. (Add a Vision key in Settings → Admin to enable auto-fill.)');
        } else {
          setScanHint(`Card photo attached — auto-fill failed (${result.error.message}). Type the details below.`);
        }
        return;
      }
      const { parsed } = result;
      const filled: string[] = [];
      const next = { ...form };
      if (parsed.name && !form.name.trim()) { next.name = parsed.name; filled.push('name'); }
      if (parsed.company && !form.company.trim()) { next.company = parsed.company; filled.push('company'); }
      if (parsed.role && !form.role.trim()) { next.role = parsed.role; filled.push('role'); }
      if (parsed.phone && !form.phone.trim()) { next.phone = parsed.phone; filled.push('phone'); }
      if (parsed.email && !form.email.trim()) { next.email = parsed.email; filled.push('email'); }
      if (parsed.website && !form.website.trim()) { next.website = parsed.website; filled.push('website'); }
      setForm(next);
      setScanHint(
        filled.length > 0
          ? `Pre-filled: ${filled.join(', ')}. The card photo will be saved with the contact.`
          : 'Card photo attached — nothing obvious to pre-fill. Type the details below.'
      );
    } finally {
      setScanning(false);
    }
  };

  const pickScanSource = () => {
    if (Platform.OS === 'web') {
      void runScan('library');
      return;
    }
    void runScan('camera');
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) {
        void notice({
          title: 'No home set up yet',
          message: 'Finish onboarding first — contacts attach to a home.',
          tone: 'warning',
        });
        return;
      }

      // Persist the card photo as a Document BEFORE the insert — slow
      // file I/O stays outside any transaction.
      let cardDocId: string | null = null;
      if (cardUri) {
        try {
          const ext = cardUri.match(/\.[a-zA-Z]+$/)?.[0] ?? '.jpg';
          const fileName = `card_${Date.now()}_${Math.random().toString(36).slice(2, 8)}${ext}`;
          const fileUri = await persistPickedFile(cardUri, fileName);
          cardDocId = await addDocument(db, {
            propertyId: prop.id,
            kind: 'photo',
            title: `Business card — ${form.name.trim() || form.company.trim() || 'contact'}`,
            file_uri: fileUri,
            mime_type: 'image/jpeg',
          });
        } catch (err) {
          console.warn('card photo persist failed', err);
        }
      }

      await addContact(db, {
        propertyId: prop.id,
        name: form.name.trim(),
        role: form.role.trim() || null,
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        website: form.website.trim() || null,
        notes: form.notes.trim() || null,
        cardDocId,
      });
      goBackOrTo(router, '/contacts');
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.name.trim().length > 0;

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      {/* Manual-vs-import chooser. "Type it in" just dismisses; import
          hands off to the Contacts page with ?import=1, which auto-opens
          its device importer. */}
      <Modal
        transparent
        animationType="fade"
        visible={sourceChooserOpen}
        onRequestClose={() => setSourceChooserOpen(false)}>
        <Pressable style={styles.chooserBackdrop} onPress={() => setSourceChooserOpen(false)}>
          <Pressable style={styles.chooserCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.chooserTitle}>Add a contact</Text>
            <Pressable
              onPress={() => router.replace('/contacts?import=1' as never)}
              style={({ pressed }) => [styles.chooserRow, pressed && { opacity: 0.9 }]}>
              <View style={styles.chooserIcon}>
                <Smartphone size={18} color={Palette.forest} strokeWidth={1.8} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.chooserRowTitle}>Import from my phone</Text>
                <Text style={styles.chooserRowSub}>Pick from your device contacts — no typing.</Text>
              </View>
            </Pressable>
            <Pressable
              onPress={() => setSourceChooserOpen(false)}
              style={({ pressed }) => [styles.chooserRow, pressed && { opacity: 0.9 }]}>
              <View style={styles.chooserIcon}>
                <PencilLine size={18} color={Palette.honeyDeep} strokeWidth={1.8} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.chooserRowTitle}>Type it in</Text>
                <Text style={styles.chooserRowSub}>Enter the details manually, or scan a business card.</Text>
              </View>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/contacts')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>New contact</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 130 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Add a trusted pro</Text>
        <Text style={styles.sub}>
          Saved here so future-you doesn&apos;t have to scroll through old texts to find them.
        </Text>

        <Pressable
          onPress={pickScanSource}
          disabled={scanning}
          style={({ pressed }) => [
            styles.scanBtn,
            pressed && { transform: [{ scale: 0.98 }] },
            scanning && { opacity: 0.6 },
          ]}>
          <ScanLine size={18} color={Palette.forest} strokeWidth={2} />
          <View style={{ flex: 1 }}>
            <Text style={styles.scanTitle}>
              {scanning ? 'Reading card…' : cardUri ? 'Re-scan the card' : 'Scan a business card'}
            </Text>
            <Text style={styles.scanSub}>
              {scanning
                ? 'Vision is parsing the text — a couple of seconds.'
                : cardUri
                ? 'Card photo attached — it will be saved with this contact.'
                : Platform.OS === 'web'
                ? 'Upload a photo — Vision pre-fills the form and keeps the card image.'
                : 'Snap the card — Vision pre-fills the form and keeps the card image.'}
            </Text>
          </View>
        </Pressable>
        {scanHint ? (
          <View style={styles.scanHint}>
            <Text style={styles.scanHintText}>{scanHint}</Text>
          </View>
        ) : null}
        {cardUri ? (
          <Image source={{ uri: cardUri }} style={styles.cardPreview} resizeMode="cover" />
        ) : null}

        <View style={styles.card}>
          <ContactForm value={form} onChange={setForm} />
        </View>
      </ScrollView>

      <StickyBottomBar>
        <Pressable
          onPress={save}
          disabled={!canSave || saving}
          style={({ pressed }) => [
            styles.saveBtn,
            { flex: 1 },
            (!canSave || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Check size={18} color={Palette.cream} strokeWidth={2} />
          <Text style={styles.saveText}>{saving ? 'Saving…' : 'Save contact'}</Text>
        </Pressable>
      </StickyBottomBar>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  chooserBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  chooserCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 18, width: '100%', maxWidth: 380, gap: 8,
  },
  chooserTitle: {
    fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink,
    marginBottom: 6,
  },
  chooserRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: 14, padding: 13,
  },
  chooserIcon: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  chooserRowTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  chooserRowSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 15,
  },
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  h1: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.three,
    fontFamily: Fonts.serif,
    fontSize: 24,
    color: Palette.ink,
    letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four,
    marginTop: 4,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
    lineHeight: 19,
  },
  card: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 20,
    padding: Spacing.four,
  },
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: Palette.forest,
    paddingVertical: 16,
    borderRadius: 18,
    marginTop: Spacing.four,
    marginHorizontal: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 15.5 },

  scanBtn: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#EAF0EA',
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: 16,
    padding: 14,
  },
  scanTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  scanSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: Palette.inkSoft,
    lineHeight: 16,
  },
  scanHint: {
    marginTop: 8,
    marginHorizontal: Spacing.four,
    padding: 12,
    borderRadius: 11,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.32)',
  },
  scanHintText: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: '#5A4012',
    lineHeight: 17,
  },
  // Business cards are landscape ~1.75:1 — show the scan at that shape
  // so the user can see it's THEIR card, not a generic thumbnail.
  cardPreview: {
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    height: 180,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Palette.line,
    backgroundColor: Palette.sandDeep,
  },
});
